'use client';

import { usePathname } from 'next/navigation';
import { useLayoutEffect, useMemo, useState } from 'react';

// Params managed by our filter system — changes to these trigger re-renders.
// Changes to other params (openedProperty, etc.) are ignored.
const FILTER_KEYS = new Set([
  'page',
  'offerType',
  'categories',
  'rooms',
  'price',
  'rentPrice',
  'unitList',
  'equipmentValue',
  'areaValue',
  'hwbClass',
  'fgeeClass',
  'locations',
  'perPage',
  'tabs',
  'sorting',
  'mapArea',
  'admunits',
  'search',
  'search-profile',
  'officeId',
  'fromDetailId',
  'name',
  'town',
  'zip',
]);

function getSearchString(): string {
  if (typeof window === 'undefined') return '';
  return window.location.search.slice(1);
}

function getFilterString(search: string): string {
  const p = new URLSearchParams(search);
  const entries: string[] = [];
  FILTER_KEYS.forEach((k) => {
    const v = p.get(k);
    if (v !== null) entries.push(`${k}=${v}`);
  });
  return entries.join('&');
}

const listeners = new Set<(isPush: boolean) => void>();
let patched = false;
let lastFilterString = '';

function notify(isPush: boolean) {
  if (typeof window === 'undefined') return;
  const current = getFilterString(window.location.search);
  if (current === lastFilterString) return;
  lastFilterString = current;
  listeners.forEach((l) => l(isPush));
}

function ensurePatched() {
  if (typeof window === 'undefined' || patched) return;
  patched = true;
  const origPush = history.pushState;
  const origReplace = history.replaceState;
  history.pushState = function (this: History, ...args: Parameters<typeof origPush>) {
    origPush.apply(this, args);
    queueMicrotask(() => notify(true));
  };
  history.replaceState = function (this: History, ...args: Parameters<typeof origReplace>) {
    origReplace.apply(this, args);
    queueMicrotask(() => notify(false));
  };
  window.addEventListener('popstate', () => notify(true));
}

function subscribe(listener: (isPush: boolean) => void) {
  ensurePatched();
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function useSearchParamsCompat(): URLSearchParams {
  const pathname = usePathname();
  const [qs, setQs] = useState('');

  useLayoutEffect(() => {
    const sync = () =>
      setQs((prev) => {
        const next = getSearchString();
        return next === prev ? prev : next;
      });
    sync();
    return subscribe(sync);
  }, [pathname]);

  return useMemo(() => new URLSearchParams(qs), [qs]);
}

export function subscribeToNavigation(listener: (isPush: boolean) => void) {
  return subscribe(listener);
}
